import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  BookOpen,
  Check,
  Ear,
  HeartHandshake,
  Loader2,
  Lock,
  Moon,
  PenLine,
  Trash2,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { LogoMark } from "@/components/logo";
import {
  ApiError,
  getRelationships,
  getStep,
  getStep4Rows,
  startStep,
  updateStep,
  wipeStep4,
  type OutgoingShare,
  type StepData,
} from "@/lib/api";
import { shortDate } from "@/lib/format";

/**
 * Step 5 — the acknowledgement archetype (Step 5 PRD + design canvas; Step 9
 * will reuse it). The step happens in a room, not here: the app holds the
 * door, records THAT it happened (sponsee self-attestation — the sponsor was
 * present, no second confirmation), and offers the Step 4 wipe. Content-free
 * by design: no field for the conversation exists, ever. No AI.
 */

type Screen = "overview" | "ack" | "bridge" | "close";

function Note({ icon: Icon = Lock, children }: { icon?: typeof Lock; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-border bg-primary/[0.06] px-4 py-3.5">
      <Icon className="mt-0.5 h-[18px] w-[18px] shrink-0 text-primary" strokeWidth={2} />
      <p className="text-[13px] leading-relaxed text-foreground">{children}</p>
    </div>
  );
}

export default function Step5() {
  const navigate = useNavigate();
  const [data, setData] = useState<StepData | null>(null);
  const [step4, setStep4] = useState<StepData | null>(null);
  const [rowCount, setRowCount] = useState(0);
  const [sponsors, setSponsors] = useState<OutgoingShare[]>([]);
  const [screen, setScreen] = useState<Screen>("overview");
  const [wiped, setWiped] = useState(false);
  const [confirmingWipe, setConfirmingWipe] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([getStep(5), getStep(4), getStep4Rows(), getRelationships().catch(() => null)])
      .then(([d5, d4, rows, rel]) => {
        setData(d5);
        setStep4(d4);
        setRowCount(rows.rows.length);
        setWiped(!!(d4.work as { wiped_at?: string | null } | null)?.wiped_at);
        if (rel) setSponsors(rel.outgoing.filter((o) => o.role === "sponsor" && o.status === "active"));
      })
      .catch((e) => {
        if (e instanceof ApiError && e.status === 401) navigate("/signin");
        else setError("Couldn't load Step 5.");
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  async function markDone() {
    setBusy(true);
    try {
      if (!data?.work) await startStep(5);
      await updateStep(5, { status: "worked", current_phase: "close" });
      // No wipe bridge if there's nothing left to wipe.
      setScreen(rowCount > 0 && !wiped ? "bridge" : "close");
    } catch {
      setError("Couldn't mark Step 5 done.");
    } finally {
      setBusy(false);
    }
  }

  async function deleteEverything() {
    setBusy(true);
    try {
      await wipeStep4();
      setWiped(true);
      setRowCount(0);
      setConfirmingWipe(false);
      setScreen("close");
    } catch {
      setError("Couldn't delete. Your writing is unchanged.");
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center gap-2 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" strokeWidth={2} />
        <span className="text-sm">Loading…</span>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 px-6 text-center">
        <p className="text-sm text-muted-foreground">{error ?? "Not available."}</p>
        <Button variant="outline" onClick={() => navigate("/steps")}>Back to Steps</Button>
      </div>
    );
  }

  /* -------- locked: Step 4 not ready yet — point back at the writing -------- */
  if (!data.available) {
    return (
      <div className="flex min-h-dvh flex-col px-6 pb-8">
        <div className="flex items-center gap-2 py-6 pl-0">
          <Button variant="ghost" size="icon" aria-label="Back" onClick={() => navigate("/steps")}>
            <ArrowLeft className="h-5 w-5" strokeWidth={2} />
          </Button>
        </div>
        <div className="animate-fade-rise flex flex-col gap-4">
          <div>
            <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
              <Lock className="h-[13px] w-[13px]" strokeWidth={2} /> Step Five · Not yet
            </p>
            <h1 className="mt-1 text-2xl font-bold text-muted-foreground">Step 5</h1>
          </div>
          <p className="text-[15px] leading-relaxed text-muted-foreground">
            Step 5 is reading your Step 4 aloud to another person. It opens when you've written
            enough to read.
          </p>
          {step4?.work && (
            <Card>
              <CardContent className="flex flex-col gap-2.5 p-4">
                <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
                  Where you are
                </p>
                <p className="flex items-center gap-2 text-sm text-foreground">
                  <PenLine className="h-3.5 w-3.5" strokeWidth={2} /> Step 4 — {rowCount} row
                  {rowCount === 1 ? "" : "s"} written so far
                </p>
                <Button variant="outline" size="lg" fullWidth onClick={() => navigate("/steps/4")}>
                  Continue Step 4
                </Button>
              </CardContent>
            </Card>
          )}
          <p className="text-[13px] text-muted-foreground">
            There's no finished. There's only ready enough to read out loud.
          </p>
          <Button variant="ghost" size="lg" fullWidth onClick={() => navigate("/steps")}>
            Return to Steps
          </Button>
        </div>
      </div>
    );
  }

  const done = data.work?.status === "worked";
  const header = (title?: string) => (
    <div className="flex items-center gap-2 px-4 pb-2 pt-6">
      <Button
        variant="ghost"
        size="icon"
        aria-label="Back"
        onClick={() => (screen === "ack" ? setScreen("overview") : navigate("/steps"))}
      >
        <ArrowLeft className="h-5 w-5" strokeWidth={2} />
      </Button>
      <span className="flex-1 text-sm font-semibold text-foreground">{title}</span>
      <span className="inline-flex items-center gap-1.5 pr-2 text-xs text-muted-foreground">
        <Users className="h-[13px] w-[13px]" strokeWidth={2} /> Happens in person
      </span>
    </div>
  );

  /* ------------------------------ overview ------------------------------ */
  if (screen === "overview" && !done) {
    const facts: [typeof BookOpen, string, string][] = [
      [BookOpen, "You read what you wrote", "All of it, in your own voice. Not a summary."],
      [Ear, "Someone else hears it", "The point is that it stops being only yours."],
      [Moon, "Then you come back here", "And mark that it happened. Nothing else."],
    ];
    const waiting = step4?.work?.status === "ready" || step4?.work?.status === "worked";
    return (
      <div className="flex min-h-dvh flex-col pb-8">
        {header()}
        <div className="animate-fade-rise flex flex-1 flex-col gap-4 px-6 pt-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
              Step Five{data.step.principle ? ` · ${data.step.principle}` : ""}
            </p>
            <h1 className="mt-1 text-2xl font-bold text-foreground">Step 5</h1>
          </div>
          <p className="text-[15px] leading-relaxed text-foreground">{data.step.intro_text}</p>
          <div className="flex flex-col gap-3">
            {facts.map(([Icon, t, d]) => (
              <div key={t} className="flex items-start gap-3">
                <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Icon className="h-[17px] w-[17px]" strokeWidth={2} />
                </span>
                <div>
                  <p className="text-sm font-semibold text-foreground">{t}</p>
                  <p className="mt-0.5 text-[13px] leading-relaxed text-muted-foreground">{d}</p>
                </div>
              </div>
            ))}
          </div>
          <Note>
            Happy24 records nothing about the conversation — not a transcript, not a note, not a
            word. There's no field here for what you said, and there never will be.
          </Note>
          {waiting && sponsors.length > 0 && (
            <Card>
              <CardContent className="flex items-center gap-3 p-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <HeartHandshake className="h-5 w-5" strokeWidth={2} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-foreground">{sponsors[0].target_email}</p>
                  <p className="text-xs text-muted-foreground">
                    Knows you're ready{step4?.work?.updated_at ? ` · marked ${shortDate(step4.work.updated_at)}` : ""}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
          <p className="text-[13px] text-muted-foreground">
            There's no scheduling, no reminder, no countdown. Take the time you take.
          </p>
          <div className="mt-auto flex flex-col gap-2 pt-4">
            <Button size="xl" fullWidth onClick={() => setScreen("ack")}>
              I've Read It to Someone
            </Button>
            <Button variant="ghost" fullWidth onClick={() => navigate("/steps")}>
              Not Yet
            </Button>
          </div>
        </div>
      </div>
    );
  }

  /* --------------------------- acknowledgement --------------------------- */
  if (screen === "ack" && !done) {
    return (
      <div className="flex min-h-dvh flex-col pb-8">
        {header("Step 5")}
        <div className="animate-fade-rise flex flex-1 flex-col gap-4 px-6 pt-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
              Step Five · Acknowledge
            </p>
            <h1 className="mt-1 text-xl font-semibold text-foreground">You read it to someone.</h1>
          </div>
          <p className="text-[15px] leading-relaxed text-foreground">
            Marking this only records that it happened, and when. Nothing about what you said goes
            anywhere.
          </p>
          <Card>
            <CardContent className="flex flex-col gap-3 p-4">
              {sponsors.length > 0 && (
                <>
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <HeartHandshake className="h-5 w-5" strokeWidth={2} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-foreground">{sponsors[0].target_email}</p>
                      <p className="text-xs text-muted-foreground">Your sponsor</p>
                    </div>
                  </div>
                  <div className="h-px bg-border" />
                </>
              )}
              <div className="flex items-center justify-between">
                <span className="text-[13px] text-muted-foreground">When</span>
                <span className="text-[13px] font-medium text-foreground">
                  Today · {shortDate(new Date().toISOString())}
                </span>
              </div>
            </CardContent>
          </Card>
          <Note icon={Users}>
            Your sponsor was there, so there's nothing for them to confirm. You mark this yourself
            — they'll see the date, and nothing else.
          </Note>
          <div className="mt-auto flex flex-col gap-2 pt-4">
            <Button size="xl" fullWidth disabled={busy} onClick={() => void markDone()}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} /> : "Mark Step 5 Done"}
            </Button>
            <Button variant="ghost" fullWidth onClick={() => setScreen("overview")}>
              Back
            </Button>
          </div>
        </div>
      </div>
    );
  }

  /* --------------------- the Step 4 wipe bridge --------------------- */
  if (screen === "bridge" || (done && screen === "overview" && rowCount > 0 && !wiped)) {
    return (
      <div className="flex min-h-dvh flex-col pb-8">
        {header("Step 5")}
        <div className="animate-fade-rise flex flex-1 flex-col gap-4 px-6 pt-6">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Check className="h-[22px] w-[22px]" strokeWidth={2} />
          </span>
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
              Step Five · Done
            </p>
            <h1 className="mt-1 text-[21px] font-bold text-foreground">You've read it aloud.</h1>
          </div>
          <p className="text-[15px] leading-relaxed text-foreground">
            Your Step 4 writing did what it was for. If you'd like, you can delete it now — your
            progress stays exactly as it is.
          </p>
          <Card>
            <CardContent className="flex flex-col gap-3 p-4">
              <div className="flex items-start gap-3">
                <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Lock className="h-[17px] w-[17px]" strokeWidth={2} />
                </span>
                <div>
                  <p className="text-sm font-semibold text-foreground">Your Step 4 inventory</p>
                  <p className="mt-0.5 text-[13px] leading-relaxed text-muted-foreground">
                    {rowCount} row{rowCount === 1 ? "" : "s"} · encrypted on this device, readable
                    only by you.
                  </p>
                </div>
              </div>
              <Button variant="outline" size="lg" fullWidth onClick={() => setConfirmingWipe(true)}>
                Delete My Step 4 Writing
              </Button>
              <p className="text-center text-xs text-muted-foreground">
                Many people do. Plenty of people keep it. Both are fine.
              </p>
            </CardContent>
          </Card>
          <div className="mt-auto pt-4">
            <Button size="xl" fullWidth onClick={() => setScreen("close")}>
              Keep It for Now
            </Button>
          </div>
        </div>
        {confirmingWipe && (
          <div className="fixed inset-0 z-50 flex items-end justify-center bg-foreground/45 p-3">
            <div className="flex w-full max-w-md flex-col gap-3 rounded-xl border border-border bg-card p-6 shadow-lg">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-status-red-bg text-status-red-text">
                <Trash2 className="h-5 w-5" strokeWidth={2} />
              </span>
              <h2 className="text-lg font-bold text-foreground">Delete your Step 4 writing?</h2>
              <p className="text-sm leading-relaxed text-foreground">
                This permanently deletes all {rowCount} row{rowCount === 1 ? "" : "s"}. Steps 4 and
                5 stay marked done and Step 6 stays open. This can't be undone.
              </p>
              <div className="mt-1 flex flex-col gap-2">
                <Button variant="destructive" size="lg" fullWidth disabled={busy} onClick={() => void deleteEverything()}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} /> : "Delete Everything"}
                </Button>
                <Button variant="ghost" size="lg" fullWidth onClick={() => setConfirmingWipe(false)}>
                  Keep My Writing
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  /* ------------------------------ close ------------------------------ */
  return (
    <div className="flex min-h-dvh flex-col px-6 pb-8 pt-6">
      <div className="animate-fade-rise flex flex-1 flex-col items-center justify-center gap-4 text-center">
        <LogoMark className="h-14 w-14 animate-scale-in" />
        <h1 className="text-2xl font-bold text-foreground">
          {wiped ? "Said out loud, and let go." : "You're not carrying it alone now."}
        </h1>
        <p className="mx-auto max-w-[296px] text-[15px] leading-relaxed text-muted-foreground">
          {wiped
            ? "Your Step 4 writing is deleted. Steps 4 and 5 stay done. What was in it is between you and your sponsor now."
            : "Someone else has heard all of it, and you're still here. Steps 4 and 5 are done. Your writing stays encrypted until you decide to delete it."}
        </p>
        <p className="mx-auto max-w-[280px] text-[13px] leading-relaxed text-muted-foreground">
          Step 6 is open whenever you want it. Today, this is enough.
        </p>
      </div>
      <div className="flex flex-col gap-2">
        <Button size="xl" fullWidth onClick={() => navigate("/steps")}>Return to Steps</Button>
        <Button variant="ghost" fullWidth onClick={() => navigate("/checkin")}>
          Back to Today's Check-In
        </Button>
      </div>
    </div>
  );
}
